import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Lock, Download, RefreshCw, LogOut, Inbox, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Admin = () => {
  const { toast } = useToast();
  const [token, setToken] = useState(sessionStorage.getItem("mc_admin") || "");
  const [password, setPassword] = useState("");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authing, setAuthing] = useState(false);

  useEffect(() => {
    document.title = "Leads Dashboard | Morrisville Communications";
  }, []);

  const fetchLeads = useCallback(async (tok) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/leads`, { headers: { "X-Admin-Token": tok } });
      setLeads(res.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        sessionStorage.removeItem("mc_admin");
        setToken("");
        toast({ title: "Session expired", description: "Please sign in again." });
      } else {
        toast({ title: "Failed to load leads", description: "Please try again." });
      }
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (token) fetchLeads(token);
  }, [token, fetchLeads]);

  const login = async (e) => {
    e.preventDefault();
    setAuthing(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      const tok = res.data.token;
      sessionStorage.setItem("mc_admin", tok);
      setToken(tok);
      setPassword("");
    } catch (e) {
      toast({ title: "Invalid password", description: "Please check and try again." });
    } finally {
      setAuthing(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem("mc_admin");
    setToken("");
    setLeads([]);
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/leads/export`, {
        headers: { "X-Admin-Token": token },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `morrisville-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      toast({ title: "Export failed", description: "Please try again." });
    }
  };

  const fmt = (d) => {
    try { return new Date(d).toLocaleString(); } catch { return d; }
  };

  if (!token) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4 brand-gradient-soft">
        <Card className="w-full max-w-md p-8 rounded-3xl shadow-xl border-slate-100 bg-white">
          <div className="h-12 w-12 rounded-xl brand-gradient flex items-center justify-center mb-5">
            <Lock className="h-6 w-6 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Leads Dashboard</h1>
          <p className="text-slate-500 text-sm mt-1 mb-6">Enter your admin password to continue.</p>
          <form onSubmit={login} className="space-y-4">
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Admin password"
              autoFocus
            />
            <Button type="submit" disabled={authing} className="w-full brand-gradient text-white rounded-full h-11 font-semibold">
              {authing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Consultation Leads</h1>
            <p className="text-slate-500 mt-1">{leads.length} total request{leads.length === 1 ? "" : "s"}</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" className="rounded-full" onClick={() => fetchLeads(token)} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button className="brand-gradient text-white rounded-full" onClick={exportCsv} disabled={!leads.length}>
              <Download className="h-4 w-4 mr-2" /> Export CSV
            </Button>
            <Button variant="outline" className="rounded-full" onClick={logout}>
              <LogOut className="h-4 w-4 mr-2" /> Sign out
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-400">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : leads.length === 0 ? (
          <Card className="p-16 rounded-2xl border-slate-100 bg-white text-center">
            <Inbox className="h-10 w-10 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No leads yet. New consultation requests will appear here.</p>
          </Card>
        ) : (
          <Card className="rounded-2xl border-slate-100 bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-left">
                  <tr>
                    <th className="px-5 py-3 font-semibold">Received</th>
                    <th className="px-5 py-3 font-semibold">Name</th>
                    <th className="px-5 py-3 font-semibold">Email</th>
                    <th className="px-5 py-3 font-semibold">Phone</th>
                    <th className="px-5 py-3 font-semibold">Company</th>
                    <th className="px-5 py-3 font-semibold">Message</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {leads.map((l) => (
                    <tr key={l.id} className="hover:bg-slate-50">
                      <td className="px-5 py-3 text-slate-500 whitespace-nowrap">{fmt(l.created_at)}</td>
                      <td className="px-5 py-3 font-medium text-slate-900">{l.name}</td>
                      <td className="px-5 py-3 text-blue-700">{l.email}</td>
                      <td className="px-5 py-3 text-slate-600">{l.phone || "-"}</td>
                      <td className="px-5 py-3 text-slate-600">{l.company || "-"}</td>
                      <td className="px-5 py-3 text-slate-600 max-w-xs">{l.message || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Admin;
